import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// frontend/src/components/AgentsBoard/AgentsBoard.tsx
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@weave-mui/material';
import { useAgents, usePatchAgent, useBatchDeleteAgents } from '../../api/hooks';
import { getErrorDetail } from '../../utils/errorDetail';
import { useBoardSelection } from '../../hooks/useBoardSelection';
import BoardHeader from '../BoardHeader/BoardHeader';
import DeleteConfirmDialog from '../BoardHeader/DeleteConfirmDialog';
import AgentCard from './AgentCard';
import AgentSidebar from './AgentSidebar';
import OrgFilter, { DEFAULT_GIT_ORGS } from './OrgFilter';
/** An agent with no githubRepos is unrestricted ("handles all"), so it matches every org
 * filter — only an agent explicitly scoped to repos outside the selected org is excluded.
 * Case-insensitive since GitHub org/repo paths are case-insensitive server-side, and
 * `githubRepos` entries are free-typed (GithubReposEditor is freeSolo, no format validation). */
function matchesOrgFilter(agent, org) {
    if (!org)
        return true;
    const repos = agent.githubRepos ?? [];
    if (repos.length === 0)
        return true;
    const orgPrefix = org.replace(/\/$/, '').toLowerCase();
    return repos.some((repo) => {
        const normalizedRepo = repo.replace(/\/$/, '').toLowerCase();
        return normalizedRepo === orgPrefix || normalizedRepo.startsWith(`${orgPrefix}/`);
    });
}
const COLUMNS = [
    { status: 'BUSY', label: 'Busy' },
    { status: 'IDLE', label: 'Idle' },
    { status: 'PAUSED', label: 'Paused' },
    { status: 'OFFLINE', label: 'Offline' },
];
/** Only IDLE <-> PAUSED are settable by drag; BUSY/OFFLINE are computed, not directly droppable. */
function droppedIsPausedFor(targetStatus) {
    if (targetStatus === 'PAUSED')
        return true;
    if (targetStatus === 'IDLE')
        return false;
    return undefined;
}
export default function AgentsBoard() {
    const navigate = useNavigate();
    const { agentId } = useParams();
    const { data, isLoading, error, refetch, isRefetching, fetchNextPage, hasNextPage, isFetchingNextPage, } = useAgents();
    const { mutate: patchAgent } = usePatchAgent();
    const { mutateAsync: batchDeleteAgents } = useBatchDeleteAgents();
    const { selectedIds, setSelectedIds, handleClick } = useBoardSelection();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [orgFilter, setOrgFilter] = useState(DEFAULT_GIT_ORGS[0]);
    const [customOrgs, setCustomOrgs] = useState([]);
    if (isLoading)
        return _jsx(CircularProgress, { sx: { m: 4 } });
    if (error) {
        return (_jsxs(Typography, { color: "error", sx: { m: 4 }, children: ["Failed to load agents: ", getErrorDetail(error)] }));
    }
    const agents = data?.results ?? [];
    const visibleAgents = agents.filter((a) => matchesOrgFilter(a, orgFilter));
    const byStatus = (status) => visibleAgents.filter((a) => a.status === status);
    const selectedAgent = agentId ? agents.find((a) => a.agentId === agentId) : undefined;
    function handleDrop(droppedAgentId, targetStatus) {
        const isPaused = droppedIsPausedFor(targetStatus);
        if (isPaused === undefined)
            return;
        patchAgent({ agentId: droppedAgentId, input: { isPaused } });
    }
    function handleOrgFilterChange(org) {
        setOrgFilter(org);
        if (org && !DEFAULT_GIT_ORGS.includes(org) && !customOrgs.includes(org)) {
            setCustomOrgs((prev) => [...prev, org]);
        }
    }
    async function handleConfirmDelete() {
        setDeleting(true);
        setDeleteError(null);
        try {
            const outcome = await batchDeleteAgents(Array.from(selectedIds));
            if (outcome.errors.length > 0) {
                setSelectedIds(new Set(outcome.errors.map((e) => e.id)));
                setDeleteError(`Failed to delete ${outcome.errors.length} of ${outcome.errors.length + outcome.results.length} agent(s). They remain selected — try again.`);
            }
            else {
                setSelectedIds(new Set());
                setDeleteDialogOpen(false);
            }
        }
        catch (e) {
            setDeleteError(`Failed to delete agents: ${getErrorDetail(e)}`);
        }
        finally {
            setDeleting(false);
        }
    }
    return (_jsxs(Box, { children: [_jsx(BoardHeader, { title: "Agents", onRefresh: () => refetch(), refreshing: isRefetching, selectedCount: selectedIds.size, onDelete: () => { setDeleteError(null); setDeleteDialogOpen(true); }, children: _jsx(OrgFilter, { value: orgFilter, onChange: handleOrgFilterChange, extraOptions: customOrgs }) }), agents.length === 0 ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', mt: 8 }, children: _jsx(Typography, { color: "text.secondary", children: "No agents online. Start the worker to see agents here." }) })) : visibleAgents.length === 0 ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', mt: 8 }, children: _jsx(Typography, { color: "text.secondary", children: "No agents match the selected GitHub org." }) })) : (_jsxs(Box, { sx: { display: 'flex', gap: 2, alignItems: 'flex-start' }, children: [_jsx(Box, { sx: { flex: 1, minWidth: 0, display: 'flex', gap: 2 }, children: COLUMNS.map(({ status, label }) => (
                        // minWidth: 0 overrides the flex item's default auto min-width (its content's
                        // max-content size) — without it, a long unwrapped title can blow the column
                        // past its equal share. Combined with the row's default align-items: stretch,
                        // this also equalizes column height regardless of card count.
                        _jsxs(Box, { sx: { flex: 1, minWidth: 0, bgcolor: '#f4f5f7', borderRadius: 2, p: 1, minHeight: 400 }, onDragOver: (e) => e.preventDefault(), onDrop: (e) => {
                                const droppedAgentId = e.dataTransfer.getData('agentId');
                                if (droppedAgentId)
                                    handleDrop(droppedAgentId, status);
                            }, children: [_jsxs(Typography, { variant: "subtitle2", sx: { mb: 1, fontWeight: 700 }, children: [label, " (", byStatus(status).length, ")"] }), byStatus(status).map((agent) => (_jsx(AgentCard, { agent: agent, selected: agentId === agent.agentId, multiSelected: selectedIds.has(agent.agentId), onClick: (a, e) => handleClick(a.agentId, status, byStatus(status).map((x) => x.agentId), e, () => navigate(`/agents/${a.agentId}`)) }, agent.agentId)))] }, status))) }), selectedAgent && (_jsx(AgentSidebar, { agent: selectedAgent, onClose: () => navigate('/agents') }))] })), hasNextPage && (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mt: 2 }, children: [_jsx(Typography, { sx: { fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: '#5e6c84' }, children: `Showing ${agents.length} loaded agent(s) — more exist` }), _jsx(Button, { variant: "outlined", onClick: () => fetchNextPage(), disabled: isFetchingNextPage, sx: {
                            borderRadius: '3px',
                            fontFamily: '"DM Sans", sans-serif',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            textTransform: 'none',
                            borderColor: '#dfe1e6',
                            color: '#5e6c84',
                        }, children: isFetchingNextPage ? 'Loading…' : 'Load More' })] })), _jsx(DeleteConfirmDialog, { open: deleteDialogOpen, count: selectedIds.size, itemLabel: "agent", onCancel: () => { setDeleteDialogOpen(false); setDeleteError(null); }, onConfirm: handleConfirmDelete, deleting: deleting, error: deleteError })] }));
}
